import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";

import type {
  ActiveIdp,
  DiscoveryState,
  PendingDiscoveryState,
} from "shell/api/types";

import { Loading } from "design-system/components/Loading/Loading";
import { ThreddLogo } from "design-system/icons";

import s from "./IdpDiscoveryContent.module.css";

interface IdpDiscoveryContentProps {
  onDefaultLogin?: () => void;
  onEmailSubmit: (email: string) => void;
  onIdpSelect?: (idp: ActiveIdp) => void;
  onReset: () => void;
  state: DiscoveryState | PendingDiscoveryState;
  testId: string;
}

export const IdpDiscoveryContent = ({
  state,
  onEmailSubmit,
  onReset,
  onIdpSelect,
  onDefaultLogin,
  testId,
}: IdpDiscoveryContentProps) => {
  const { t } = useTranslation(["common"]);

  return (
    <Flex
      justify="center"
      align="center"
      className={s.container}
      data-testid={testId}
    >
      <Card className={s.card}>
        <ThreddLogo
          width={160}
          height={64}
          role="img"
          aria-label="Thredd"
          className={s.logo}
        />

        {state.step === "email_input" && (
          <Form<{ email: string }>
            onFinish={({ email }) => onEmailSubmit(email)}
            layout="vertical"
          >
            <Form.Item
              name="email"
              label={t("idpDiscovery.emailLabel")}
              rules={[
                { required: true, message: t("labels.emailRequired") },
                { type: "email", message: t("labels.emailInvalid") },
              ]}
            >
              <Input
                type="email"
                placeholder={t("idpDiscovery.emailPlaceholder")}
                size="large"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item>
              <Button
                className={s.submitButton}
                type="primary"
                htmlType="submit"
                block
                size="large"
              >
                {t("idpDiscovery.continue")}
              </Button>
            </Form.Item>
          </Form>
        )}

        {state.step === "loading" && (
          <Flex justify="center" className={s.loadingContainer}>
            <Loading size="large" />
          </Flex>
        )}

        {state.step === "idp_selection" && (
          <Flex vertical gap="middle" className={s.fullWidth}>
            <Typography.Text type="secondary">
              {t("idpDiscovery.signingInAs", { email: state.email })}
            </Typography.Text>
            <Button type="link" onClick={onReset} className={s.changeEmailLink}>
              {t("idpDiscovery.useDifferentEmail")}
            </Button>
            {state.idps.map(idp => (
              <Button
                key={idp.cognitoProviderName}
                block
                size="large"
                onClick={() => onIdpSelect?.(idp)}
              >
                {idp.name} <Tag>{idp.type}</Tag>
              </Button>
            ))}
            <Divider>{t("idpDiscovery.or")}</Divider>
            <Button block size="large" onClick={onDefaultLogin}>
              {t("idpDiscovery.signInWithPassword")}
            </Button>
          </Flex>
        )}

        {state.step === "error" && (
          <Flex vertical gap="middle" className={s.fullWidth}>
            <Alert type="warning" title={state.message} showIcon />
            <Button block onClick={onReset}>
              {t("idpDiscovery.tryAnotherEmail")}
            </Button>
            <Divider>{t("idpDiscovery.or")}</Divider>
            <Button block size="large" onClick={onDefaultLogin}>
              {t("idpDiscovery.signInWithPassword")}
            </Button>
          </Flex>
        )}
      </Card>
    </Flex>
  );
};
